import React, { useRef, useState, useEffect } from 'react';
import { Avatar, IconButton, Stack, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useModal } from '../../../components';
import SubscribeModal from '../../../components/common/SubscribeModal';
import useIsAdmin from '../../../hooks/useIsAdmin';
import useSubscription from '../../../hooks/useSubscription';
import { useAuth } from '../../../components/auth/AuthProvider';

type AvatarUploaderProps = {
  src?: string;
  onFileUpload?: (file: File) => void;
  label?: string;
};

const AvatarUploader = ({
  src,
  onFileUpload,
  label = 'Click camera icon to upload',
}: AvatarUploaderProps) => {
  const { openModal, closeModal } = useModal();
  const [avatar, setAvatar] = useState<string | null>(src || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentUser } = useAuth();
  const { isAdmin } = useIsAdmin(currentUser);
  const { isPremium } = useSubscription();

  const isPaidUser = isPremium || isAdmin;

  const handleIconClick = () => {
    if (!isPaidUser) {
      return openModal(
        <SubscribeModal
          closeModal={closeModal}
          handleClose={closeModal}
          message={`Add family member's profile picture`}
          title="Upload family member's picture is unavailable on the free plan"
        />,
      );
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (src) {
      setAvatar(src);
    }
  }, [src]);

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setAvatar(reader.result as string);
      if (onFileUpload) {
        onFileUpload(file);
      }
      reader.readAsDataURL(file);
    }
  };

  return (
    <Stack direction="column" spacing={2} alignItems="center">
      <div style={{ position: 'relative', width: 100, height: 100 }}>
        <Avatar
          src={avatar ?? undefined}
          alt="User Avatar"
          sx={{ width: 100, height: 100 }}
        />
        <IconButton
          onClick={handleIconClick}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            backgroundColor: 'background.paper',
            border: '1px solid lightgray',
            '&:hover': {
              backgroundColor: 'secondary.main',
            },
          }}
          size="small"
        >
          <PhotoCameraIcon fontSize="small" />
        </IconButton>
      </div>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
      />
      <Typography variant="body2" color="text.secondary" align="center">
        {label}
      </Typography>
    </Stack>
  );
};

export default AvatarUploader;
